/**
 * @file aiConsent.ts
 * @brief "How VERBLIZR uses AI" agreement (Apple 5.1.2(i), BACKEND B7):
 *        parsing the server's state and wording, and when to ask.
 *
 * @details
 * The text lives in the backend (`wording`, versioned); the client renders it
 * as is and posts back the version it showed. `required` = this user must
 * agree before a recording, interpretation, Ask or import. A missing state
 * (route not deployed yet, read error) means "don't ask yet": the server's
 * 403 AI_CONSENT_REQUIRED still catches anything that slips through.
 * Pure (only the pure publicConfig module) so node:test can load it. The
 * privacy link falls back to PRIVACY_URL from GET /api/public/config (B23),
 * else the old link.
 *
 * @references
 * - BACKEND/src/features/auth/aiConsent.js (B7.1)
 * - scratch/claude-code-report.md "## B7" §1
 *
 * @author DES (prepared with Claude)
 * @date 22-10-2026
 */

import { FALLBACKS, configValue, publicConfigSnapshot } from "../../shared/publicConfig/publicConfig.ts";

export const AI_CONSENT_REQUIRED = "AI_CONSENT_REQUIRED";
export const AI_CONSENT_VERSION_STALE = "AI_CONSENT_VERSION_STALE";

export type AiConsentWording = {
  version: string;
  title: string;
  body: string;
  agreeLabel: string;
  declineLabel: string;
  privacyUrl: string;
};

export type AiConsentState = {
  accepted: boolean;
  required: boolean;
  acceptedVersion: string | null;
  acceptedAt: string | null;
  enforcement: string;
  wording: AiConsentWording;
};

const str = (v: unknown): string | null => (typeof v === "string" && v.trim() ? v : null);

export function parseAiConsentWording(raw: unknown): AiConsentWording | null {
  if (!raw || typeof raw !== "object") return null;
  const w = raw as Record<string, unknown>;
  const version = str(w.version);
  const title = str(w.title);
  const body = str(w.body);
  if (!version || !title || !body) return null;
  return {
    version,
    title,
    body,
    agreeLabel: str(w.agreeLabel) ?? "Agree",
    declineLabel: str(w.declineLabel) ?? "Not now",
    privacyUrl: str(w.privacyUrl) ?? configValue(publicConfigSnapshot(), "PRIVACY_URL", FALLBACKS.PRIVACY_URL),
  };
}

/** GET/POST /api/auth/ai-consent (or `/me.aiConsent`) → state, or null. */
export function parseAiConsentState(raw: unknown): AiConsentState | null {
  if (!raw || typeof raw !== "object") return null;
  const s = raw as Record<string, unknown>;
  const wording = parseAiConsentWording(s.wording);
  if (!wording || typeof s.accepted !== "boolean") return null;
  return {
    accepted: s.accepted,
    required: s.required === true,
    acceptedVersion: str(s.acceptedVersion),
    acceptedAt: str(s.acceptedAt),
    enforcement: str(s.enforcement) ?? "aware",
    wording,
  };
}

/** Ask before starting? Only when the server says this user must agree. */
export function shouldAskFirst(state: AiConsentState | null): boolean {
  return !!state && state.required && !state.accepted;
}

/** The wording carried by a 403 AI_CONSENT_REQUIRED or 409 STALE body. */
export function wordingFromRefusal(body: unknown): AiConsentWording | null {
  if (!body || typeof body !== "object") return null;
  return parseAiConsentWording((body as { consent?: unknown }).consent);
}

/** Data & sharing caption: "Agreed on 22 Oct 2026" or "Not agreed yet". */
export function agreedCaption(state: AiConsentState, formatDate: (iso: string) => string): string {
  if (!state.accepted) return "Not agreed yet. We'll ask before your first recording.";
  return state.acceptedAt ? `Agreed on ${formatDate(state.acceptedAt)}` : "Agreed";
}
